import { useState, useEffect } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';
import { formatDateAU } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { 
  ArrowLeft, BookOpen, ExternalLink, Calendar, Eye, Plus, Pencil
} from 'lucide-react';
import DocumentPreviewModal from '@/components/DocumentPreviewModal';
import AdviceModal from '@/components/AdviceModal';
import { toast } from 'sonner';

export default function ClientAdvice() {
  const { client, clientId } = useOutletContext();
  const { token } = useAuth();
  const navigate = useNavigate();
  
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [showDocModal, setShowDocModal] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);

  useEffect(() => {
    const fetchDocs = async () => {
      if (!token || !clientId) return;
      try {
        const response = await api.getAdvice(token, clientId);
        setDocs(response.data);
      } catch (error) {
        console.error('Failed to fetch advice docs:', error);
      }
      setLoading(false);
    };
    fetchDocs();
  }, [token, clientId]);

  const handleAddDoc = () => {
    setEditingDoc(null);
    setShowDocModal(true);
  };

  const handleEditDoc = (doc) => {
    setEditingDoc(doc);
    setShowDocModal(true);
  };

  const handleSaveDoc = (docData) => {
    if (editingDoc) {
      // Edit existing document
      setDocs(prev => prev.map(d => 
        d.id === editingDoc.id 
          ? { ...d, ...docData }
          : d
      ));
      toast.success('Advice document updated successfully');
    } else {
      // Create new document
      const newDoc = {
        id: `advice-${clientId.split('-')[1]}-${Date.now()}`,
        clientId,
        ...docData
      };
      setDocs(prev => [...prev, newDoc]);
      toast.success('Advice document added successfully');
    }
    setShowDocModal(false);
    setEditingDoc(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-2 border-[#4A3B32] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in-up" data-testid="advice-page">
      {/* Page Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <Button 
            variant="ghost" 
            onClick={() => navigate(`/app/clients/${clientId}/overview`)}
            className="caffeine-btn-ghost mb-2"
            data-testid="back-to-overview-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to overview
          </Button>
          <h1 className="font-heading text-2xl text-[#2C2420]">Advice</h1>
          <p className="text-[#5D524D]">Advice documents for {client?.name}</p>
        </div>
        <Button 
          className="caffeine-btn" 
          onClick={handleAddDoc}
          data-testid="add-advice-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Advice
        </Button>
      </div>

      {/* Note */}
      <div className="bg-[#4A3B32]/5 border border-[#4A3B32]/20 rounded-lg p-4 mb-6">
        <p className="text-sm text-[#5D524D]">
          <strong>Note:</strong> Advice items stored securely; sources are indexed.
        </p>
      </div>

      {/* Advice Cards */}
      {docs.length > 0 ? (
        <div className="space-y-4">
          {docs.map(doc => (
            <div key={doc.id} className="caffeine-card p-5" data-testid={`advice-doc-${doc.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#4A3B32]/10 rounded-lg flex items-center justify-center">
                    <BookOpen className="w-5 h-5 text-[#4A3B32]" />
                  </div>
                  <div>
                    <h3 className="font-medium text-[#2C2420] mb-1">{doc.title}</h3>
                    <div className="flex items-center gap-3 text-sm text-[#8C8078]">
                      {doc.type && (
                        <span className="px-2 py-0.5 rounded text-xs bg-[#4A3B32]/10 text-[#4A3B32]">
                          {doc.type}
                        </span>
                      )}
                      <span>Adviser: {doc.adviser}</span>
                      <span>•</span>
                      <span>Date: {formatDateAU(doc.date)}</span>
                    </div>
                    {doc.reviewDue && (
                      <div className="flex items-center gap-2 mt-2 text-sm text-[#D99A6C]">
                        <Calendar className="w-4 h-4" />
                        <span>Review due: {formatDateAU(doc.reviewDue)}</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="source-chip">{doc.sourceSystem}</span>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    className="caffeine-btn-ghost"
                    onClick={() => setPreviewDoc(doc)}
                    data-testid={`preview-advice-doc-${doc.id}`}
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    Preview
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    className="caffeine-btn-ghost"
                    onClick={() => handleEditDoc(doc)}
                    data-testid={`edit-advice-doc-${doc.id}`}
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    className="caffeine-btn-ghost"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="empty-state">
          <BookOpen className="w-12 h-12" />
          <p className="text-lg font-medium text-[#5D524D]">No advice documents</p>
          <p className="text-sm">No advice documents indexed for this client</p>
          <Button 
            className="caffeine-btn mt-4" 
            onClick={handleAddDoc}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add First Advice Document
          </Button>
        </div>
      )}

      {/* Document Preview Modal */}
      {previewDoc && (
        <DocumentPreviewModal
          document={previewDoc}
          type="advice"
          onClose={() => setPreviewDoc(null)}
        />
      )}

      {/* Add/Edit Advice Modal */}
      {showDocModal && (
        <AdviceModal
          document={editingDoc}
          onClose={() => {
            setShowDocModal(false);
            setEditingDoc(null);
          }}
          onSave={handleSaveDoc}
        />
      )}
    </div>
  );
}
